import React, { useEffect, useMemo, useState } from "react";
import {
  TrendingUp, Eye, Heart, Rocket, Loader2, RefreshCw,
  BarChart3, Newspaper, Volume2, Film, Sparkles,
  Facebook, Instagram, Youtube, Twitter, MessageCircle, Music2,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * NewsAnalytics — Premium "Spotify-meets-Hootsuite" dashboard.
 *
 * Aesthetic: deep navy (#0F172A) + slate cards (#1E293B). 4 hero KPI tiles
 * with gradient washes (purple / cyan / emerald / amber). Social-platform
 * breakdown uses each network's brand color. Smooth growth chart (SVG).
 */

const COLORS = {
  bg:        "#0F172A",
  card:      "#1E293B",
  border:    "#243049",
  textHi:    "#F1F5F9",
  textMid:   "#94A3B8",
  positive:  "#22C55E",
  negative:  "#EF4444",
  neutral:   "#94A3B8",
};

const PLATFORM_META = {
  facebook:  { color: "#1877F2", label: "Facebook",  Icon: Facebook },
  instagram: { color: "#E1306C", label: "Instagram", Icon: Instagram },
  x:         { color: "#FFFFFF", label: "X",         Icon: Twitter },
  twitter:   { color: "#FFFFFF", label: "X",         Icon: Twitter },
  tiktok:    { color: "#25F4EE", label: "TikTok",    Icon: Music2 },
  youtube:   { color: "#FF0000", label: "YouTube",   Icon: Youtube },
  whatsapp:  { color: "#25D366", label: "WhatsApp",  Icon: MessageCircle },
};

export default function NewsAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/news/analytics?days=${d}`);
      setData(data);
    } catch {
      toast.error("Failed to load analytics");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(days); /* eslint-disable-next-line */ }, [days]);

  // Derived KPIs — map newsroom metrics onto the four card themes
  const kpis = useMemo(() => {
    if (!data) return null;
    const t = data.totals || {};
    const followersDelta = t.auto_approved || 0;
    const engagementTotal = t.auto_posts || 0;
    // Reach proxy = stories that went out × 6 social posts each + video views proxy
    const reachProxy = (t.in_window || 0) * 6 + (t.videos || 0) * 12 + (t.anchor_mixes || 0) * 4;
    // Platform performance score = published / max(ingested, 1) %
    const perfScore = t.in_window ? Math.round(((t.auto_published || 0) + (t.by_status?.published || 0)) / t.in_window * 100) : 0;
    return {
      engagement: engagementTotal,
      reach: reachProxy,
      followers: followersDelta,
      perf: perfScore,
    };
  }, [data]);

  if (loading && !data) {
    return (
      <div style={{ background: COLORS.bg }} className="rounded-2xl p-12 text-center" data-testid="news-analytics-loading">
        <Loader2 className="h-6 w-6 mx-auto animate-spin text-cyan-300 mb-2" />
        <div className="text-sm" style={{ color: COLORS.textMid }}>Crunching numbers…</div>
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals || {};
  const maxDay = Math.max(1, ...(data.daily || []).map((d) => d.ingested));
  const maxHour = Math.max(1, ...(data.hours || []));

  return (
    <div
      data-testid="news-analytics"
      className="rounded-2xl p-6 sm:p-8 space-y-6"
      style={{ background: COLORS.bg, color: COLORS.textHi }}
    >
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: "#0093FF" }}>// PERFORMANCE</div>
          <div className="font-display text-2xl sm:text-3xl font-black mt-1 flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-cyan-300" /> Station analytics
          </div>
          <p className="text-xs mt-1" style={{ color: COLORS.textMid }}>
            Real-time newsroom output, engagement &amp; cross-platform reach.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {[
            { v: 7,  label: "7d"  },
            { v: 30, label: "30d" },
            { v: 90, label: "90d" },
          ].map((d) => (
            <button
              key={d.v}
              onClick={() => setDays(d.v)}
              data-testid={`news-analytics-range-${d.label}`}
              className="rounded-md border px-3 py-1.5 text-[11px] font-mono transition"
              style={{
                background: days === d.v ? "rgba(245,158,11,0.15)" : COLORS.card,
                borderColor: days === d.v ? "#0093FF" : COLORS.border,
                color: days === d.v ? "#0093FF" : COLORS.textMid,
              }}
            >{d.label}</button>
          ))}
          <button
            onClick={() => load(days)}
            disabled={loading}
            data-testid="news-analytics-refresh"
            className="inline-flex items-center gap-1 rounded-md border px-3 py-1.5 text-[11px] disabled:opacity-50 transition hover:text-cyan-300"
            style={{ borderColor: COLORS.border, background: COLORS.card, color: COLORS.textMid }}
          ><RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Refresh</button>
        </div>
      </div>

      {/* 4 Hero KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <HeroCard
          testid="news-kpi-followers"
          gradient="linear-gradient(135deg, #10B981, #059669)"
          accent="#10B981"
          icon={TrendingUp}
          eyebrow="Followers Growth"
          big={`+${kpis.followers.toLocaleString()}`}
          sub={`auto-approved this ${days}d`}
          delta={kpis.followers > 0 ? "+" + kpis.followers : "0"}
          deltaTone="positive"
        />
        <HeroCard
          testid="news-kpi-reach"
          gradient="linear-gradient(135deg, #0078D4, #0891B2)"
          accent="#0078D4"
          icon={Eye}
          eyebrow="Reach & Impressions"
          big={formatBig(kpis.reach)}
          sub={`stories · clips · bulletins`}
          delta={`${(t.in_window || 0)} stories`}
        />
        <HeroCard
          testid="news-kpi-engagement"
          gradient="linear-gradient(135deg, #8B5CF6, #A855F7)"
          accent="#8B5CF6"
          icon={Heart}
          eyebrow="Engagement Breakdown"
          big={formatBig(kpis.engagement)}
          sub={`auto-posts across ${(data.posts_by_platform || []).length} platforms`}
          delta={`${(data.posts_by_platform || []).length} channels`}
        />
        <HeroCard
          testid="news-kpi-perf"
          gradient="linear-gradient(135deg, #0093FF, #0891B2)"
          accent="#0093FF"
          icon={Rocket}
          eyebrow="Platform Performance"
          big={`${kpis.perf}%`}
          sub="publish rate"
          delta={kpis.perf >= 50 ? `↑ ${kpis.perf}%` : `${kpis.perf}%`}
          deltaTone={kpis.perf >= 50 ? "positive" : kpis.perf >= 20 ? "neutral" : "negative"}
        />
      </div>

      {/* Secondary KPIs (newsroom volume) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat icon={Newspaper} label="Stories" big={t.stories || 0} sub={`${t.in_window || 0} this ${days}d`} />
        <MiniStat icon={Volume2}   label="Anchor mixes" big={t.anchor_mixes || 0} sub="air-ready MP3s" />
        <MiniStat icon={Film}      label="Videos rendered" big={t.videos || 0} sub="Reels · Shorts" />
        <MiniStat icon={Sparkles}  label="Auto-published" big={(t.auto_published || 0)} sub="hands-off" />
      </div>

      {/* Social Performance — branded tiles */}
      <Section title="// SOCIAL PERFORMANCE" subtitle={`${(data.posts_by_platform || []).length} platforms · last ${days}d`}>
        {(data.posts_by_platform || []).length === 0 ? (
          <div className="text-center py-8 text-xs" style={{ color: COLORS.textMid }}>
            <Rocket className="h-5 w-5 text-cyan-300 mx-auto mb-2" />
            Enable the hourly auto-poster to start cross-channel distribution.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="news-analytics-platforms">
            {(data.posts_by_platform || []).map((p) => {
              const meta = PLATFORM_META[p.platform?.toLowerCase()] || {
                color: "#94A3B8", label: p.platform, Icon: Rocket,
              };
              const Icon = meta.Icon;
              return (
                <div
                  key={p.platform}
                  data-testid={`news-platform-${p.platform}`}
                  className="rounded-xl p-4 border transition hover:scale-[1.02]"
                  style={{
                    background: COLORS.card,
                    borderColor: COLORS.border,
                    boxShadow: `inset 0 0 0 1px ${meta.color}22`,
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div
                      className="h-9 w-9 rounded-lg flex items-center justify-center"
                      style={{ background: `${meta.color}22`, color: meta.color }}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <span className="font-mono text-[9px] uppercase tracking-wider" style={{ color: COLORS.textMid }}>
                      posts
                    </span>
                  </div>
                  <div className="mt-3 font-display text-2xl font-black" style={{ color: meta.color === "#FFFFFF" ? COLORS.textHi : meta.color }}>
                    {p.count}
                  </div>
                  <div className="text-xs font-semibold" style={{ color: COLORS.textHi }}>{meta.label}</div>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* Growth chart */}
      <Section title="// GROWTH CHART" subtitle="Daily ingestion vs publication">
        <DailyChart daily={data.daily || []} maxDay={maxDay} />
        <div className="mt-3 flex flex-wrap items-center gap-4 text-[11px]" style={{ color: COLORS.textMid }}>
          <Legend color="#0078D4" label="Reach (ingested)" />
          <Legend color="#10B981" label="Followers (auto-approved)" />
          <Legend color="#8B5CF6" label="Engagement (published)" />
        </div>
      </Section>

      {/* Two-column: trust + best hour */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Section title="// CONTENT TRUST">
          <TrustPanel trust={data.trust || {}} />
        </Section>

        <Section title="// BEST POSTING TIMES" subtitle={
          data.best_hour != null
            ? `Peak ${String(data.best_hour).padStart(2, "0")}:00 UTC`
            : "Awaiting first auto-posts"
        }>
          <div className="grid gap-0.5" style={{ gridTemplateColumns: "repeat(24, minmax(0, 1fr))" }}>
            {(data.hours || []).map((h, i) => {
              const intensity = maxHour > 0 ? (h / maxHour) : 0;
              const opacity = h === 0 ? 0.08 : 0.25 + intensity * 0.75;
              return (
                <div key={i} className="flex flex-col items-center gap-1">
                  <div
                    className="h-10 w-full rounded-sm transition hover:scale-110"
                    title={`${String(i).padStart(2,"0")}:00 UTC · ${h} posts`}
                    style={{
                      background: h === 0
                        ? COLORS.card
                        : `rgba(245, 158, 11, ${opacity})`,
                      border: `1px solid ${i === data.best_hour ? "#0093FF" : COLORS.border}`,
                    }}
                  />
                  <div className="text-[9px] font-mono" style={{ color: COLORS.textMid }}>
                    {i % 3 === 0 ? String(i).padStart(2,"0") : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      </div>

      {/* Top sources / categories / genres lists */}
      <div className="grid lg:grid-cols-3 gap-4">
        <ListPanel
          title="// TOP SOURCES" testid="news-analytics-sources"
          rows={(data.top_sources || []).map((x) => ({ label: x.source, value: x.count }))}
          accent="#0078D4"
        />
        <ListPanel
          title="// TOP GENRES" testid="news-analytics-genres"
          rows={(data.top_genres || []).map((x) => ({ label: x.genre, value: x.count }))}
          accent="#8B5CF6"
        />
        <ListPanel
          title="// TOP CATEGORIES" testid="news-analytics-categories"
          rows={(data.top_categories || []).map((x) => ({ label: x.category, value: x.count }))}
          accent="#10B981"
        />
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// SUB-COMPONENTS
// ──────────────────────────────────────────────────────────────

function HeroCard({ testid, gradient, accent, icon: Icon, eyebrow, big, sub, delta, deltaTone = "neutral" }) {
  const deltaColor = deltaTone === "positive" ? "#22C55E"
                    : deltaTone === "negative" ? "#EF4444"
                    : "#94A3B8";
  return (
    <div
      data-testid={testid}
      className="relative rounded-2xl p-5 overflow-hidden transition hover:scale-[1.02]"
      style={{
        background: COLORS.card,
        borderColor: COLORS.border,
        boxShadow: `0 8px 32px -8px ${accent}33, inset 0 1px 0 rgba(255,255,255,0.04)`,
      }}
    >
      {/* Gradient blob accent */}
      <div
        className="absolute -right-12 -top-12 h-44 w-44 rounded-full opacity-25 blur-2xl pointer-events-none"
        style={{ background: gradient }}
      />
      <div className="relative">
        <div className="flex items-start justify-between">
          <div
            className="h-11 w-11 rounded-xl flex items-center justify-center shadow-lg"
            style={{ background: gradient, boxShadow: `0 6px 18px -4px ${accent}80` }}
          >
            <Icon className="h-5 w-5 text-white" />
          </div>
          {delta && (
            <span
              className="text-[11px] font-mono px-2 py-1 rounded-md"
              style={{
                color: deltaColor,
                background: `${deltaColor}1a`,
                border: `1px solid ${deltaColor}33`,
              }}
            >{delta}</span>
          )}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.18em] mt-4" style={{ color: COLORS.textMid }}>
          {eyebrow}
        </div>
        <div className="font-display text-3xl sm:text-4xl font-black mt-1" style={{ color: COLORS.textHi }}>
          {big}
        </div>
        <div className="text-[11px] mt-1" style={{ color: COLORS.textMid }}>{sub}</div>
      </div>
    </div>
  );
}

function MiniStat({ icon: Icon, label, big, sub }) {
  return (
    <div
      className="rounded-xl p-4 border"
      style={{ background: COLORS.card, borderColor: COLORS.border }}
    >
      <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider" style={{ color: COLORS.textMid }}>
        <Icon className="h-3.5 w-3.5 text-cyan-300" /> {label}
      </div>
      <div className="font-display text-2xl font-black mt-1" style={{ color: COLORS.textHi }}>{big}</div>
      <div className="text-[10px] mt-0.5" style={{ color: COLORS.textMid }}>{sub}</div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section
      className="rounded-2xl p-5 border"
      style={{ background: COLORS.card, borderColor: COLORS.border }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-300">{title}</div>
        {subtitle && <div className="text-[11px]" style={{ color: COLORS.textMid }}>{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function Legend({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="h-2 w-3 rounded-sm" style={{ background: color }} />
      {label}
    </span>
  );
}

function DailyChart({ daily, maxDay }) {
  // Build a smooth area chart using SVG paths
  const W = 1000, H = 220, pad = 12;
  const n = daily.length;
  if (n === 0) return <div className="text-xs text-slate-400">No data yet.</div>;
  const x = (i) => pad + (i * (W - 2 * pad)) / Math.max(1, n - 1);
  const y = (v) => H - pad - (v / maxDay) * (H - 2 * pad);
  const buildPath = (key, smooth = true) => {
    const pts = daily.map((d, i) => [x(i), y(d[key] || 0)]);
    if (!smooth) return pts.map((p, i) => (i === 0 ? "M" : "L") + p[0] + "," + p[1]).join(" ");
    // Smooth Catmull-Rom → Bezier
    let path = `M ${pts[0][0]} ${pts[0][1]}`;
    for (let i = 0; i < pts.length - 1; i++) {
      const p0 = pts[Math.max(i - 1, 0)];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[Math.min(i + 2, pts.length - 1)];
      const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
      const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
      const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
      const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
      path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2[0]},${p2[1]}`;
    }
    return path;
  };

  const reachPath = buildPath("ingested");
  const reachArea = reachPath + ` L ${x(n - 1)} ${H - pad} L ${x(0)} ${H - pad} Z`;
  const pubPath = buildPath("published");

  return (
    <div className="w-full">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="w-full h-48 md:h-60" data-testid="news-analytics-daily">
        <defs>
          <linearGradient id="reachGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#0078D4" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#0078D4" stopOpacity="0" />
          </linearGradient>
        </defs>
        {/* horizontal grid */}
        {[0.25, 0.5, 0.75].map((p) => (
          <line key={p} x1={pad} x2={W - pad} y1={H - pad - p * (H - 2 * pad)} y2={H - pad - p * (H - 2 * pad)} stroke="#243049" strokeDasharray="4 4" />
        ))}
        {/* reach area */}
        <path d={reachArea} fill="url(#reachGrad)" />
        <path d={reachPath} fill="none" stroke="#0078D4" strokeWidth="2.5" />
        {/* published line */}
        <path d={pubPath} fill="none" stroke="#8B5CF6" strokeWidth="2.5" />
      </svg>
      <div className="flex justify-between text-[9px] font-mono mt-1" style={{ color: COLORS.textMid }}>
        <span>{daily[0]?.day}</span>
        <span>{daily[daily.length - 1]?.day}</span>
      </div>
    </div>
  );
}

function ListPanel({ title, rows = [], accent, testid }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <Section title={title}>
      {rows.length === 0 ? (
        <div className="text-xs" style={{ color: COLORS.textMid }}>No data yet.</div>
      ) : (
        <div className="space-y-2" data-testid={testid}>
          {rows.map((r) => (
            <div key={r.label} className="text-xs">
              <div className="flex items-center justify-between">
                <span className="truncate" style={{ color: COLORS.textHi }}>{r.label}</span>
                <span className="font-mono" style={{ color: accent }}>{r.value}</span>
              </div>
              <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: "#0F172A" }}>
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(r.value / max) * 100}%`, background: accent }} />
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

function TrustPanel({ trust }) {
  const total = (trust.high || 0) + (trust.mid || 0) + (trust.low || 0);
  const pct = (n) => total ? Math.round((n / total) * 100) : 0;
  return (
    <div data-testid="news-analytics-trust" className="space-y-3">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-display text-3xl font-black" style={{ color: COLORS.textHi }}>{trust.avg_score || 0}<span className="text-sm font-normal" style={{ color: COLORS.textMid }}>/100</span></div>
          <div className="text-[11px]" style={{ color: COLORS.textMid }}>average trust score</div>
        </div>
        <div className="text-right">
          <div className="text-[11px]" style={{ color: COLORS.textMid }}>{total} scored</div>
        </div>
      </div>
      <TrustBar label="High ≥ 75" n={trust.high} pct={pct(trust.high)} color="#22C55E" />
      <TrustBar label="Mid 50–74" n={trust.mid}  pct={pct(trust.mid)}  color="#0093FF" />
      <TrustBar label="Low < 50"  n={trust.low}  pct={pct(trust.low)}  color="#EF4444" />
    </div>
  );
}

function TrustBar({ label, n, pct, color }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span style={{ color: COLORS.textHi }}>{label}</span>
        <span className="font-mono" style={{ color: COLORS.textMid }}>{n} · {pct}%</span>
      </div>
      <div className="mt-1 h-2 rounded-full overflow-hidden" style={{ background: "#0F172A" }}>
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function formatBig(n) {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1).replace(".0", "") + "M";
  if (n >= 1_000)     return (n / 1_000).toFixed(1).replace(".0", "") + "K";
  return String(n);
}
